/**
 * Bridge constructor logic — the heart of the demo.
 *
 * Models the `GradedBridge` type from chapter 4 of the manuscript:
 * a tuple `(grade, body, license)` where `license` is a constructive
 * proof that `body` carries the unit/counit data indexed by `grade`.
 *
 * Models the SNF chain `B1 → B2 → B3 → B4` from chapter 4 §4.4 and
 * the typed admission rule from §4.2. Verdicts here are exactly the
 * structural refusals T1–T4 from `00-Theorems.md`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  componentKey,
  detectIcgEnvelope,
  formatGrade,
  gradeAdd,
  gradeEquals,
  isNeutral,
  neutralGrade,
  sdpIndex,
  sdpIndexEquals,
  singletonGrade,
  type Grade,
  type Phase,
  type SdpCode,
} from "./grade.js";
import type { ImmunityClosureGuaranteeName } from "@csk/core";

/**
 * A bridge "body" — the payload an attacker can shape. In the demo
 * the body records which (SDP, phase) pairs it actually witnesses.
 *
 * The `license` is then a derived proof that the chosen `grade`
 * vector lines up component-by-component with `witnesses`.
 */
export interface BridgeBody {
  readonly id: string;
  readonly witnesses: readonly {
    readonly sdp: SdpCode;
    readonly phase: Phase;
  }[];
}

/** A graded bridge: body + grade + license proof status. */
export interface GradedBridge {
  readonly body: BridgeBody;
  readonly grade: Grade;
  /**
   * `true` iff every grade component the user lit corresponds to a
   * witness in the body, and every body witness corresponds to a lit
   * grade component (biconditional from chapter 3 §3.3.1).
   */
  readonly licenseInhabited: boolean;
}

/** Compute the natural grade derived from a body's witnesses. */
export function deriveGrade(body: BridgeBody): Grade {
  const out = { ...neutralGrade() } as Record<string, 0 | 1>;
  for (const w of body.witnesses) {
    out[componentKey(w.sdp, w.phase)] = 1;
  }
  return Object.freeze(out);
}

/**
 * Check whether the `license` field is inhabited for the given body
 * and claimed grade.
 *
 * The license is inhabited iff the claimed grade equals the grade
 * derived from the body. This is the biconditional from chapter 3
 * §3.3.1: lighting a grade component requires a matching witness,
 * and a witness requires a matching lit component.
 */
export function licenseInhabited(body: BridgeBody, grade: Grade): boolean {
  return gradeEquals(grade, deriveGrade(body));
}

/** Construct a graded bridge with derived license status. */
export function makeGradedBridge(body: BridgeBody, grade: Grade): GradedBridge {
  return {
    body,
    grade,
    licenseInhabited: licenseInhabited(body, grade),
  };
}

/** A seam's grade obligation: `seamGrade : Seam → G`. */
export interface Seam {
  readonly id: string;
  readonly label: string;
  readonly expectedGrade: Grade;
}

/**
 * The admission verdict the type-checker would emit when a
 * `GradedBridge` is presented at a `Seam`. Matches the manuscript's
 * structural refusal vocabulary (T1, T2, etc.) rather than runtime
 * detection language.
 */
export type AdmissionVerdict =
  | {
      readonly outcome: "ADMITTED";
      readonly seam: Seam;
      readonly bridge: GradedBridge;
      readonly explanation: string;
    }
  | {
      readonly outcome: "REFUSED";
      readonly seam: Seam;
      readonly bridge: GradedBridge;
      readonly refusedBy: "T1" | "T2" | "license-uninhabited";
      readonly explanation: string;
    };

/**
 * Attempt to admit a graded bridge at a seam. Mirrors the typed
 * admit function from chapter 4 §4.2:
 *
 *     admit : (σ : Seam)
 *           → (b : GradedBridge A B)
 *           → grade b ≡ seamGrade σ
 *           → AdmittedAt σ
 */
export function admit(bridge: GradedBridge, seam: Seam): AdmissionVerdict {
  if (!bridge.licenseInhabited) {
    return {
      outcome: "REFUSED",
      seam,
      bridge,
      refusedBy: "license-uninhabited",
      explanation:
        "The bridge's license field is uninhabited — the claimed grade " +
        `${formatGrade(bridge.grade)} does not match the body's actual ` +
        `witnesses ${formatGrade(deriveGrade(bridge.body))}. The bridge ` +
        "cannot even be formed as a GradedBridge inhabitant; this is " +
        "structurally distinct from a seam-level refusal.",
    };
  }
  if (isNeutral(bridge.grade)) {
    return {
      outcome: "REFUSED",
      seam,
      bridge,
      refusedBy: "T1",
      explanation:
        `T1 (ungraded-empty): the bridge sits at the neutral grade 0_G. ` +
        "By T1 no admissible bridge has the neutral grade as its sole grade, " +
        "regardless of what the seam expects — the position is uninhabited.",
    };
  }
  if (!gradeEquals(bridge.grade, seam.expectedGrade)) {
    return {
      outcome: "REFUSED",
      seam,
      bridge,
      refusedBy: "T2",
      explanation:
        `T2 (grade-reflected): the bridge carries ${formatGrade(bridge.grade)} but the ` +
        `seam ${seam.label} expects ${formatGrade(seam.expectedGrade)}. ` +
        "Grade-distinct bridges have disjoint inhabitants — the type-checker " +
        "cannot witness both grades on the same body.",
    };
  }
  return {
    outcome: "ADMITTED",
    seam,
    bridge,
    explanation:
      `Bridge ${formatGrade(bridge.grade)} matches seam obligation ` +
      `${formatGrade(seam.expectedGrade)} exactly. The admit function ` +
      "constructs an AdmittedAt σ inhabitant.",
  };
}

/**
 * Compose two graded bridges with composable endpoints. The
 * composite grade is `g₁ + g₂ ∈ G`; the manuscript's chapter 5
 * §5.3 four-case verdict applies.
 */
export type CompositionVerdict =
  | {
      readonly outcome: "COMPOSED";
      readonly composite: GradedBridge;
      readonly caseLabel: "B (ICG envelope)" | "C (cross-SDP)";
      readonly icgSdp: SdpCode | null;
      readonly icg: ImmunityClosureGuaranteeName | null;
      readonly explanation: string;
    }
  | {
      readonly outcome: "REFUSED";
      readonly refusedBy: "T1" | "input-uninhabited";
      readonly caseLabel: "A (replay)" | "D (vacuous)";
      readonly explanation: string;
    };

export function composeGraded(
  b1: GradedBridge,
  b2: GradedBridge,
): CompositionVerdict {
  if (!b1.licenseInhabited || !b2.licenseInhabited) {
    return {
      outcome: "REFUSED",
      refusedBy: "input-uninhabited",
      caseLabel: "D (vacuous)",
      explanation:
        "One of the input bridges has an uninhabited license; composition is " +
        "vacuous because the input is not a typable GradedBridge.",
    };
  }
  if (isNeutral(b1.grade) || isNeutral(b2.grade)) {
    return {
      outcome: "REFUSED",
      refusedBy: "T1",
      caseLabel: "D (vacuous)",
      explanation:
        "By T1, no admissible bridge has the neutral grade as its individual " +
        "grade. Case D of chapter 5 §5.3 is vacuous.",
    };
  }
  const composite = gradeAdd(b1.grade, b2.grade);
  if (isNeutral(composite)) {
    return {
      outcome: "REFUSED",
      refusedBy: "T1",
      caseLabel: "A (replay)",
      explanation:
        "Case A of chapter 5 §5.3: the two grades are equal, so g₁ + g₂ = 0_G " +
        "(ℤ/2 self-cancellation). The composite grade is the neutral grade, " +
        "which by T1 has no admissible bridge. The replay is unconstructable.",
    };
  }
  const composedBody: BridgeBody = {
    id: `${b1.body.id}∘${b2.body.id}`,
    witnesses: [...b1.body.witnesses, ...b2.body.witnesses].filter(
      (w, i, arr) => {
        const k = componentKey(w.sdp, w.phase);
        const last = arr.findIndex(
          (other) => componentKey(other.sdp, other.phase) === k,
        );
        const count = arr.filter(
          (other) => componentKey(other.sdp, other.phase) === k,
        ).length;
        return count % 2 === 1 && i === last;
      },
    ),
  };
  const composedBridge = makeGradedBridge(composedBody, composite);
  // Case B vs Case C is decided by the *composite*, not by the input
  // shapes. If the composite hits an ICG envelope `[s, η] + [s, ε]` for
  // some SDP s, that is Case B; everything else is Case C. This handles
  // the edge case where one operand is already an ICG envelope: e.g.
  // [Dt, η] + [Dt, ε] composed with [Dt, η] simplifies to [Dt, ε], a
  // single-phase composite that is decidedly NOT an ICG envelope.
  const envelope = detectIcgEnvelope(composite);
  if (envelope !== null) {
    return {
      outcome: "COMPOSED",
      composite: composedBridge,
      caseLabel: "B (ICG envelope)",
      icgSdp: envelope.sdp,
      icg: envelope.icg,
      explanation:
        `Case B of chapter 5 §5.3: composite is the ${envelope.icg} envelope ` +
        `grade [${envelope.sdp}, η] + [${envelope.sdp}, ε] — the both-phases-discharged ` +
        "certificate.",
    };
  }
  return {
    outcome: "COMPOSED",
    composite: composedBridge,
    caseLabel: "C (cross-SDP)",
    icgSdp: null,
    icg: null,
    explanation:
      "Case C of chapter 5 §5.3: composite is a cross-SDP grade vector. " +
      "Admissible only at a seam whose obligation matches the full " +
      "composite — typically a cross-SDP envelope.",
  };
}

/**
 * SNF phase check — does the (B2.grade, B4.grade) pair satisfy T4
 * phase-index diagonality?
 */
export interface SnfChain {
  readonly b1Witness: string;
  readonly b2Grade: Grade;
  readonly b3Composite: Grade;
  readonly b4Grade: Grade;
}

export type SnfVerdict =
  | { readonly outcome: "VALID"; readonly explanation: string }
  | {
      readonly outcome: "INVALID";
      readonly refusedBy: "T4" | "B3-mismatch";
      readonly explanation: string;
    };

export function checkSnf(chain: SnfChain): SnfVerdict {
  const expectedB3 = gradeAdd(chain.b2Grade, chain.b4Grade);
  if (!gradeEquals(chain.b3Composite, expectedB3)) {
    return {
      outcome: "INVALID",
      refusedBy: "B3-mismatch",
      explanation:
        "B3 (EXECUTE) should carry the composite ε∘η = [s, η] + [s, ε]. " +
        `Got ${formatGrade(chain.b3Composite)}; expected ${formatGrade(expectedB3)}.`,
    };
  }
  if (!sdpIndexEquals(sdpIndex(chain.b2Grade), sdpIndex(chain.b4Grade))) {
    return {
      outcome: "INVALID",
      refusedBy: "T4",
      explanation:
        "T4 (phase-index diagonality): sdpIndex(B2) ≠ sdpIndex(B4). " +
        "The cross-SDP audit-log forgery has no inhabitant.",
    };
  }
  return {
    outcome: "VALID",
    explanation:
      "SNF chain is well-formed: B2 unit phase and B4 counit phase agree on " +
      "their SDP index (T4), and B3 carries the composite ε∘η. The bridge " +
      "is a typable inhabitant of GradedBridge.",
  };
}

/** Helper: build the canonical single-SDP SNF chain for an SDP. */
export function singleSdpSnf(sdp: SdpCode): SnfChain {
  return {
    b1Witness: `evidence-for-${sdp}`,
    b2Grade: singletonGrade(sdp, "eta"),
    b3Composite: gradeAdd(
      singletonGrade(sdp, "eta"),
      singletonGrade(sdp, "epsilon"),
    ),
    b4Grade: singletonGrade(sdp, "epsilon"),
  };
}
